export const DEFAULT_RADIUS_KM = 100;
export const DISCOVERY_RADIUS_PRESETS = [25, 50, 100, 150, 200, 500];
export const DISCOVERY_URL_KEYS = [
  "lat", "lng", "place", "radius", "type", "styles", "access", "difficulty",
  "dry", "open", "minTrails", "maxDrive", "minRating", "rideMode",
];

const TYPES = ["everything", "bikepark", "trailcenter", "trail"];
const STYLES = ["downhill", "enduro", "flow", "freeride", "jump", "xc", "family"];
const ACCESS = ["lift", "shuttle", "pedal", "ebike"];
const DIFFICULTY = ["easy", "intermediate", "advanced", "expert"];

const parseNumber = (value, fallback = null) => {
  if (value == null || String(value).trim() === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseList = (value, allowed) => value
  ? value.split(",").map((item) => item.trim()).filter((item) => allowed.includes(item))
  : [];

const validRadius = (value) => Number.isFinite(value) && value >= 1 && value <= 1000;

export const readDiscoveryUrlState = (searchParams, rideModes = []) => {
  const lat = parseNumber(searchParams.get("lat"));
  const lng = parseNumber(searchParams.get("lng"));
  const place = searchParams.get("place")?.trim() || "";
  const legacyBrokenCenter = lat === 0 && lng === 0 && place === "Selected location";
  const parsedRadius = parseNumber(searchParams.get("radius"), DEFAULT_RADIUS_KM);
  const radius = validRadius(parsedRadius) ? parsedRadius : DEFAULT_RADIUS_KM;
  const typeParam = searchParams.get("type");
  const type = TYPES.includes(typeParam) ? typeParam : "everything";

  return {
    hasCenter: lat != null && lng != null && !legacyBrokenCenter,
    center: lat != null && lng != null && !legacyBrokenCenter
      ? { lat, lng, name: place || "Selected location" }
      : null,
    query: legacyBrokenCenter ? "" : place,
    radius,
    type,
    styles: parseList(searchParams.get("styles"), STYLES),
    access: parseList(searchParams.get("access"), ACCESS),
    difficulty: parseList(searchParams.get("difficulty"), DIFFICULTY),
    dryWeather: searchParams.get("dry") === "1",
    openNow: searchParams.get("open") === "1",
    minTrails: Math.max(0, parseNumber(searchParams.get("minTrails"), 0)),
    maxDrive: Math.max(0, parseNumber(searchParams.get("maxDrive"), 0)),
    minRating: Math.max(0, parseNumber(searchParams.get("minRating"), 0)),
    rideMode: rideModes.includes(searchParams.get("rideMode")) ? searchParams.get("rideMode") : null,
  };
};

export const buildDiscoveryUrlParams = ({
  searchCenter,
  origin,
  radius = DEFAULT_RADIUS_KM,
  type = "everything",
  styles = [],
  access = [],
  difficulty = [],
  dryWeather = false,
  openNow = false,
  minTrails = 0,
  maxDrive = 0,
  minRating = 0,
  rideMode = "usual",
}) => {
  const next = new URLSearchParams();

  if (searchCenter) {
    next.set("lat", String(Number(searchCenter.lat.toFixed?.(6) ?? searchCenter.lat)));
    next.set("lng", String(Number(searchCenter.lng.toFixed?.(6) ?? searchCenter.lng)));
    const label = searchCenter.name === "this map area"
      ? "Map area"
      : (origin?.name || searchCenter.name);
    if (label) next.set("place", label);
    next.set("radius", String(validRadius(radius) ? radius : DEFAULT_RADIUS_KM));
  }

  if (type !== "everything") next.set("type", type);
  if (styles.length) next.set("styles", styles.join(","));
  if (access.length) next.set("access", access.join(","));
  if (difficulty.length) next.set("difficulty", difficulty.join(","));
  if (dryWeather) next.set("dry", "1");
  if (openNow) next.set("open", "1");
  if (minTrails) next.set("minTrails", String(minTrails));
  if (maxDrive) next.set("maxDrive", String(maxDrive));
  if (minRating) next.set("minRating", String(minRating));
  if (rideMode && rideMode !== "usual") next.set("rideMode", rideMode);

  return next;
};

const normalizedList = (value, transform = (item) => item) =>
  Array.isArray(value) ? [...new Set(value.map(transform))] : null;

export const applyInterpretedCriteria = (current, criteria = {}, {
  removedCriteria = [],
  resetSearch = false,
} = {}) => {
  const next = resetSearch
    ? {
      ...current,
      origin: null,
      searchCenter: null,
      query: "",
      radius: DEFAULT_RADIUS_KM,
      type: "everything",
      styles: [],
      access: [],
      difficulty: [],
      dryWeather: false,
      openNow: false,
      minTrails: 0,
      maxDrive: 0,
      minRating: 0,
    }
    : {
      ...current,
      styles: [...(current.styles || [])],
      access: [...(current.access || [])],
      difficulty: [...(current.difficulty || [])],
    };

  const removed = new Set(removedCriteria || []);
  if (removed.has("origin")) {
    next.origin = null;
    next.searchCenter = null;
    next.query = "";
    next.radius = DEFAULT_RADIUS_KM;
  }
  if (removed.has("radiusKm")) next.radius = DEFAULT_RADIUS_KM;
  if (removed.has("type")) next.type = "everything";
  if (removed.has("styles")) next.styles = [];
  if (removed.has("access")) next.access = [];
  if (removed.has("difficulty")) next.difficulty = [];
  if (removed.has("minTrails")) next.minTrails = 0;
  if (removed.has("minRating")) next.minRating = 0;
  if (removed.has("openNow")) next.openNow = false;

  if (criteria.origin && Number.isFinite(criteria.origin.lat) && Number.isFinite(criteria.origin.lng)) {
    const center = {
      lat: criteria.origin.lat,
      lng: criteria.origin.lng,
      name: criteria.origin.label || "AI search origin",
    };
    next.origin = center;
    next.searchCenter = center;
    next.query = center.name;
  }
  if (validRadius(criteria.radius_km)) next.radius = criteria.radius_km;
  if (TYPES.includes(criteria.type)) next.type = criteria.type;

  const styles = normalizedList(criteria.styles);
  if (styles) next.styles = styles.filter((item) => STYLES.includes(item));

  const access = normalizedList(criteria.access);
  if (access) next.access = access.filter((item) => ACCESS.includes(item));

  const difficulty = normalizedList(criteria.difficulty, (item) => String(item).toLowerCase());
  if (difficulty) next.difficulty = difficulty.filter((item) => DIFFICULTY.includes(item));

  if (Number.isInteger(criteria.min_trails) && criteria.min_trails >= 0) next.minTrails = criteria.min_trails;
  if (Number.isFinite(criteria.min_rating) && criteria.min_rating >= 0 && criteria.min_rating <= 5) next.minRating = criteria.min_rating;
  if (typeof criteria.open_now === "boolean") next.openNow = criteria.open_now;

  return next;
};

export const explicitInterpretedCriteriaKeys = (criteria = {}) => {
  const keys = [];
  if (criteria.origin) keys.push("origin");
  if (validRadius(criteria.radius_km)) keys.push("radius");
  if (TYPES.includes(criteria.type)) keys.push("type");
  if (Array.isArray(criteria.styles)) keys.push("styles");
  if (Array.isArray(criteria.access)) keys.push("access");
  if (Array.isArray(criteria.difficulty)) keys.push("difficulty");
  if (Number.isInteger(criteria.min_trails)) keys.push("minTrails");
  if (Number.isFinite(criteria.min_rating)) keys.push("minRating");
  if (typeof criteria.open_now === "boolean") keys.push("openNow");
  return keys;
};


export const discoveryCriteriaContext = ({
  searchCenter,
  radius = DEFAULT_RADIUS_KM,
  type = "everything",
  styles = [],
  access = [],
  difficulty = [],
  openNow = false,
  minTrails = 0,
  minRating = 0,
} = {}) => {
  const criteria = {};
  if (searchCenter && Number.isFinite(searchCenter.lat) && Number.isFinite(searchCenter.lng)) {
    criteria.origin = {
      label: searchCenter.name || "Current search area",
      lat: searchCenter.lat,
      lng: searchCenter.lng,
    };
    if (validRadius(radius)) criteria.radius_km = radius;
  }
  if (type !== "everything") criteria.type = type;
  if (styles.length) criteria.styles = [...styles];
  if (access.length) criteria.access = [...access];
  if (difficulty.length) criteria.difficulty = difficulty.map((item) => String(item).toUpperCase());
  if (openNow) criteria.open_now = true;
  if (minTrails) criteria.min_trails = minTrails;
  if (minRating) criteria.min_rating = minRating;
  return criteria;
};
